import { appAlert } from '@/lib/appAlert';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { fontSize } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { fetchConversion } from '@/lib/exchangeRates';

/**
 * Ενδεικτική μετατροπή ποσού σε EUR/USD δίπλα σε ποσά με "ξένο" νόμισμα —
 * καθαρά πληροφοριακό (δεν υπάρχει πραγματική συναλλαγή/μεταφορά μέσα στην
 * εφαρμογή), βοηθάει τον αντισυμβαλλόμενο να καταλάβει το μέγεθος του ποσού
 * όταν σκέφτεται σε άλλο νόμισμα από τον δημιουργό της συμφωνίας.
 */
export function CurrencyHint({ amount, currency }: { amount: number | null | undefined; currency: string | null | undefined }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = makeStyles(colors);
  const [conversion, setConversion] = useState<{ EUR: number; USD: number } | null>(null);

  const cur = (currency ?? 'EUR').toUpperCase();
  // Το EUR είναι το κοινό/προεπιλεγμένο νόμισμα και για τα δύο μέρη μέσα στην
  // εφαρμογή — δεν έχει νόημα να δείχνουμε ισοτιμία σε $ για ποσά που είναι
  // ήδη σε EUR, μόνο για πραγματικά "ξένα" νομίσματα.
  const showEur = cur !== 'EUR';
  const showUsd = cur !== 'USD' && cur !== 'EUR';

  useEffect(() => {
    let cancelled = false;
    if (amount == null || !(showEur || showUsd)) { setConversion(null); return; }
    void fetchConversion(cur).then((r) => { if (!cancelled) setConversion(r); });
    return () => { cancelled = true; };
  }, [cur, amount, showEur, showUsd]);

  if (amount == null || !conversion) return null;

  const parts: string[] = [];
  if (showEur && Number.isFinite(conversion.EUR)) parts.push(`${(amount * conversion.EUR).toFixed(2)} €`);
  if (showUsd && Number.isFinite(conversion.USD)) parts.push(`${(amount * conversion.USD).toFixed(2)} $`);
  if (parts.length === 0) return null;

  return (
    <Pressable onPress={() => appAlert(t('common.info'), t('currencyHint.disclaimer'))} accessibilityRole="button">
      <Text style={styles.text}>≈ {parts.join(' / ')}</Text>
    </Pressable>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    text: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      marginTop: 2,
    },
  });
}
